'use client'

import { ShoppingBasket } from 'cms-types'
import { useRouter } from 'next/navigation'
import { ComponentProps, useCallback, useEffect, useMemo, useState } from 'react'
import { addAlert, ShoppingBasket as UIShoppingBasket } from 'ui'

import { getCurrentAndNextTier } from '@/cms/helpers'
import { getLocaleFromHostname } from '@/helpers/getLocaleFromHostname'
import { logger } from '@/helpers/logger'
import { MAX_PER_ITEM_IN_CART, useCart } from '@/store/cartStore'
import { useCustomer } from '@/store/customerStore'
import { useMetaObjects } from '@/store/metaObjectStore'

type ShoppingBasketClientWrapperProps = Omit<ShoppingBasket, 'upSell'> & {
  upSell: ComponentProps<typeof UIShoppingBasket>['upSell']
  benefitsTitles?: Record<string, string>
}

function ShoppingBasketClientWrapper({
  upSell,
  title,
  checkoutButtonText,
  totalPriceText,
  deleteItemText,
  accessability,
  activeDiscountsText,
  benefitsTitles,
}: ShoppingBasketClientWrapperProps) {
  const cart = useCart()
  const customer = useCustomer(state => state.customer)
  const tierDefinitions = useMetaObjects(s => s.tierDefinitions)
  const getTierDefinitions = useMetaObjects(s => s.getTierDefinitions)

  const router = useRouter()
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    getTierDefinitions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { currentTier } = useMemo(
    () => getCurrentAndNextTier(tierDefinitions, customer?.points),
    [tierDefinitions, customer?.points],
  )

  // Discount codes coming from the logged-in customer's tier benefits are translated
  // by Shopify (via the @inContext language directive on the customer/tier fetch). Map
  // them by their discount code so we can prefer the Shopify-translated benefit title.
  // This covers the customer's individual/one-time/used benefits as well as the
  // permanent benefits of the tier the customer is currently in.
  const benefitDiscountTitles = useMemo(() => {
    const map: Record<string, string> = {}
    if (customer == null) return map

    for (const benefit of [
      ...customer.benefitsIndividual,
      ...customer.benefitsOnetime,
      ...customer.benefitsUsed,
      ...(currentTier?.permanentBenefits ?? []),
    ]) {
      if (benefit.discountCode && benefit.title) {
        map[benefit.discountCode.toUpperCase()] = benefit.title
      }
    }

    return map
  }, [customer, currentTier])

  const onClose = useCallback(() => {
    cart.setIsOpen(false)
  }, [cart])

  const onUpdateItemQuantity = useCallback(
    async (data: { quantity: number; productId: string; variantId: string }[]) => {
      setIsLoading(true)

      try {
        await cart.updateQuantity(
          data.map(({ variantId, quantity }) => ({ variantId, quantity, mode: 'set' })),
        )
      } catch (e) {
        logger.error('ShoppingBasket > onUpdateItemQuantity', e)

        addAlert.error('Fehler beim Aktualisieren des Produkts')
      } finally {
        setIsLoading(false)
      }
    },
    [cart],
  )

  const onItemDelete = useCallback(
    async ({ variantId }: { variantId?: string }) => {
      setIsLoading(true)

      try {
        if (variantId == null) return
        await cart.removeFromBasket([variantId])
      } catch (e) {
        logger.error('ShoppingBasket > onItemDelete', e)

        addAlert.error('Fehler beim Entfernen des Produkts')
      } finally {
        setIsLoading(false)
      }
    },
    [cart],
  )

  const onAddUpSellProduct = useCallback(async () => {
    if (upSell?.isEnabled !== true) return

    const variantId = upSell.variant.id
    setIsLoading(true)

    try {
      await cart.addToBasket([{ variantId }])
    } catch (e) {
      logger.error('ShoppingBasket > onAddUpSellProduct', e)

      addAlert.error('Fehler beim Hinzufügen eines Produkts')
    } finally {
      setIsLoading(false)
    }
  }, [cart, upSell])

  const onCheckoutSubmit = useCallback(() => {
    if (!cart.data) return

    const url = new URL(cart.data.checkoutUrl)
    url.searchParams.set('t', Date.now().toString())
    url.searchParams.set('locale', getLocaleFromHostname(window.location.hostname))

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const klaviyoId = (window._learnq as any).push(['_getIdentifiers'])['$exchange_id']
      url.searchParams.set('klaviyoId', klaviyoId)
    } catch (e) {
      logger.warn('ShoppingBasket > onCheckoutSubmit > klavyio', e)
    }

    router.push(url.toString())
  }, [cart, router])

  const activeDiscounts =
    cart.data?.discountCodes.map(({ code }) => {
      const key = code.toUpperCase()
      // Prefer the Shopify-translated tier benefit title, otherwise fall back to the
      // CMS translation, and finally to the raw discount code.
      return benefitDiscountTitles[key] ?? benefitsTitles?.[key] ?? code
    }) ?? []

  return (
    <UIShoppingBasket
      isOpen={cart.isOpen}
      maxPerItemInCart={MAX_PER_ITEM_IN_CART}
      title={title}
      isLoading={isLoading}
      checkoutButtonText={checkoutButtonText}
      totalPriceText={totalPriceText}
      deleteItemText={deleteItemText}
      onClose={onClose}
      cart={cart.data}
      onItemQuantityUpdate={onUpdateItemQuantity}
      onItemDelete={onItemDelete}
      onCheckoutSubmit={onCheckoutSubmit}
      {...(upSell?.isEnabled === true ? { upSell: upSell, onAddUpSellProduct } : upSell)}
      accessability={accessability}
      activeDiscountsText={activeDiscountsText}
      activeDiscounts={activeDiscounts}
    />
  )
}

export default ShoppingBasketClientWrapper
